import React, { useState } from 'react';
import { BsTwitterX } from "react-icons/bs";
import { FaMeta } from "react-icons/fa6";
import aliceImage from './images/Alice.jpg';
import coquiLogo from './images/coqui.webp';
import MobileNav from './components/MobileNav';
import NewsTicker from './components/NewsTicker';
import { useEffect, lazy, Suspense } from 'react';


const LazyVideoModal  = lazy(() => import('./components/VideoModal'));
// const MemoizedNewsTicker = memo(NewsTicker);

const App = () => {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Preload critical images
  useEffect(() => {
    const imageLoader = new Image();
    imageLoader.src = aliceImage;
    // imageLoader.onload = () => setImagesLoaded(true);
  }, []);

  // Render optimization function
  const shouldRenderHeavyEffects = () => {
    // Only render heavy effects on Chrome
    const isChrome = 
      navigator.userAgent.indexOf("Chrome") !== -1 &&
      navigator.userAgent.indexOf("Edge") === -1;
    
    return isChrome;
  };

  const navItems = [
    { label: 'News', href: '#top' },
    { label: 'Shows', href: '#top' },
    { label: 'Tech', href: '#top' },
    { 
      label: 'Check News!', 
      href: 'https://twitter.com/nonfungiblenews',
      isExternal: true,
      isCTA: true 
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Background layers */}
      <div 
        className="fixed inset-0 w-full h-full bg-cover bg-center bg-no-repeat z-0"
        style={{
          backgroundImage: 'url("/img/nfn.webp")',
          backgroundBlendMode: shouldRenderHeavyEffects() ? 'overlay' : 'normal'
        }}
      >
        <div className="absolute inset-0 bg-black/70" />
      </div>

      {/* Header Navigation */}
      <nav className="fixed w-full z-50 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="nav-container">
            <div className="flex items-center gap-5">
              <div className="nav-logo">
                <img
                  src="logoNFN.png"
                  alt="Non-Fungible News"
                  className="h-16 w-auto"
                />
              </div>
              <span className="logo-text text-5xl font-bold hidden sm:block">
                <a href="#top" rel="noopener noreferrer">NON-FUNGIBLE NEWS</a>
              </span>
            </div>
            
            {/* Desktop Navigation */}
            <div className="nav-menu hidden lg:flex">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  className={`nav-item ${item.isCTA ? 'nav-cta' : ''}`}
                  href={item.href}
                  target={item.isExternal ? "_blank" : "_self"}
                  rel={item.isExternal ? "noopener noreferrer" : ""}
                >
                  {item.label}
                </a>
              ))}
            </div>

            {/* Mobile Navigation */}
            <MobileNav
              isOpen={isMobileMenuOpen}
              toggleMenu={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {navItems.map((item) => (
                <a
                  key={item.label}
                  className="text-2xl text-white hover:text-fuchsia-400 transition-colors"
                  href={item.href}
                  target={item.isExternal ? "_blank" : "_self"}
                  rel={item.isExternal ? "noopener noreferrer" : ""}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </a>
              ))}
            </MobileNav>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <header className="header">
        <div className="inset-x-0 top-24 bottom-24 bg-black/30 backdrop-blur-sm z-20 rounded-2xl max-w-[1400px] mx-auto">
        <div className="left-0 right-0">
            <NewsTicker />
        </div>
          <div className="header-container relative px-4">
            <div className="header-content">
              <h1 className="main-title text-4xl md:text-5xl lg:text-6xl">
                Everything you need<br />
                to know about<br />
                <span className="highlight-box">Crypto & AI</span>
              </h1>
              <h2 className="sub-title font-['BlenderPro']">
                Meet Alice, the World's First AI Journalist!
              </h2>

              <div className="cta-container sm:flex-row sm:space-y-0 sm:space-x-4">
                <a className="cta-button primary sm:w-auto" 
                href='https://twitter.com/nonfungiblenews' target="_blank" rel="noopener noreferrer">
                  <BsTwitterX className="w-5 h-5 mr-2" />
                  Follow Now
                </a>
                <button 
                  className="cta-button secondary"
                  onClick={() => setIsVideoModalOpen(true)}
                >
                  Watch Alice
                </button>
              </div>

              <div className="platform-info">
                <div className="nfn-description">
                  High-quality news and analysis delivered by 
                  Alice Kopper on X, world's first synthetic journalist.
                </div>
              </div>
            </div>
            
            <div className="header-image-container mt-12 lg:mt-0">
              <div className="absolute -inset-2 bg-gradient-to-r from-blue-600 to-fuchsia-600 opacity-75 blur-xl" />
              <img 
                src={aliceImage}
                alt="Alice"
                className="header-image"
              />
              {/* <div className="image-overlay" /> */}
            </div>
          </div>
        </div>

        {/* <div className="bottom-gradient" /> */}
      </header>

      {/* Gradient overlays */}
      <div className="fixed inset-0 bg-gradient-to-t from-purple-900/20 to-transparent mix-blend-overlay pointer-events-none z-10" />
      <div className="fixed inset-0 bg-gradient-to-b from-blue-900/20 to-transparent mix-blend-overlay pointer-events-none z-10" />
      
      {/* Desktop Footer */}
      <footer className="relative z-30 border-t border-fuchsia-600/30 hidden lg:block">
        <div className="max-w-7xl mx-auto px-6 py-12 sm:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-10 sm:gap-x-8 sm:gap-y-12">
            <div className="space-y-3">
              <h3 className="text-lg sm:text-xl font-bold text-fuchsia-400">About NFN</h3>
              <ul className="space-y-3">
                <li>
                  <a href='#top' className="footer-content">
                    About Us
                  </a>
                </li>
                <li>
                  <a href='#top' className="footer-content">
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg sm:text-xl font-bold text-fuchsia-400">Resources</h3>
              <ul className="space-y-3">
                <li>
                  <a href='#top' className="footer-content">
                    Newsletter
                  </a>
                </li>
              </ul>
            </div>

            <div className="space-y-3">
              <h3 className="text-lg sm:text-xl font-bold text-fuchsia-400">Connect</h3>
              <div className="flex items-center space-x-4">
                <a 
                  href="https://twitter.com/nonfungiblenews" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-white transition-colors p-2 -ml-2"
                >
                  <BsTwitterX className="w-5 h-5 sm:w-6 sm:h-6" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Universal Bottom Footer (visible on all screen sizes) */}
      <div className="fixed bottom-0 left-0 right-0 z-30 border-t border-fuchsia-600/30 bg-black/60 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col items-center space-y-2 sm:flex-row sm:justify-between sm:space-y-0">
            <p className="text-gray-400 text-xs sm:text-sm text-center">
              &copy; 2024 Non-Fungible News. All rights reserved.
            </p>
            <div className="flex items-center space-x-2">
              <span className="text-gray-400 text-xs sm:text-sm">Powered by</span>
              <FaMeta className="w-4 h-4 text-gray-400" />
              <img
                src={coquiLogo}
                alt="OpenAI Logo"
                className="w-4 h-4 object-contain"
              />
              
            </div>
          </div>
        </div>
      </div>

      {isVideoModalOpen && (
        <Suspense fallback={<div className="loading-spinner">Loading...</div>}>
          <LazyVideoModal 
            isOpen={isVideoModalOpen} 
            onClose={() => setIsVideoModalOpen(false)} 
          />
        </Suspense>
      )}
    </div>
  );
};

export default App;